// Phase 2 — API Keys management page.
//
// Admin creates / lists / revokes bearer tokens that the MCP server
// (Phase 4) and the REST tool mirror will use to authenticate AI
// assistants calling the read-only tool surface.
//
// Plaintext key is shown ONCE in a modal immediately after creation —
// never retrievable from the list again. The warning text + copy
// affordance is intentionally prominent so the operator doesn't lose
// the key between creation and configuring their Claude client.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Copy, Check, Plus, Trash2 } from "lucide-react";
import { api, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function KeyStatus({ k }) {
  if (k.revoked_at) {
    return (
      <Badge variant="secondary" data-testid={`status-revoked-${k.key_id}`}>
        revoked
      </Badge>
    );
  }
  return (
    <Badge variant="default" data-testid={`status-active-${k.key_id}`}>
      active
    </Badge>
  );
}

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null); // {plaintext, name, prefix}
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null); // key doc
  const [revoking, setRevoking] = useState(false);

  const sorted = useMemo(() => {
    // Backend already returns created_at desc; just defend against
    // out-of-order rows from a stale cache.
    return [...keys].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
  }, [keys]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/keys");
      setKeys(res.data?.keys || []);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitCreate = async () => {
    const name = nameDraft.trim();
    if (!name) {
      toast.error("Name is required.");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/keys", { name });
      setJustCreated({
        plaintext: res.data.plaintext,
        name: res.data.name,
        prefix: res.data.prefix,
      });
      setCreateOpen(false);
      setNameDraft("");
      await load();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setCreating(false);
    }
  };

  const copyPlaintext = async () => {
    if (!justCreated?.plaintext) return;
    try {
      await navigator.clipboard.writeText(justCreated.plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select the key manually and copy.");
    }
  };

  const submitRevoke = async () => {
    if (!confirmRevoke) return;
    setRevoking(true);
    try {
      await api.delete(`/keys/${confirmRevoke.key_id}`);
      toast.success(`Revoked "${confirmRevoke.name}".`);
      setConfirmRevoke(null);
      await load();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="api-keys-page">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <Link
            to="/admin"
            data-testid="back-to-admin"
            className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-brand"
          >
            <ChevronLeft className="h-4 w-4" /> Admin
          </Link>
          <h1 className="text-3xl font-semibold tracking-tight">API Keys</h1>
          <p className="text-sm text-neutral-600 max-w-2xl">
            Bearer tokens used by Claude and other AI assistants to call
            the read-only tool surface. Keys are shown in plaintext ONCE
            at creation — copy it immediately. Revoke to disable access.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          data-testid="create-key-button"
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-2" /> Create key
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All keys</CardTitle>
          <CardDescription>
            {loading
              ? "Loading…"
              : sorted.length === 0
                ? "No keys yet. Create one to let an AI assistant connect."
                : `${sorted.length} total · newest first`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sorted.length > 0 && (
            <Table data-testid="keys-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((k) => (
                  <TableRow key={k.key_id} data-testid={`key-row-${k.key_id}`}>
                    <TableCell className="font-medium">{k.name}</TableCell>
                    <TableCell className="font-mono text-xs text-neutral-600">
                      gos_{k.prefix}…
                    </TableCell>
                    <TableCell className="text-sm text-neutral-600">
                      {formatDate(k.created_at)}
                    </TableCell>
                    <TableCell className="text-sm text-neutral-600">
                      {formatDate(k.last_used_at)}
                    </TableCell>
                    <TableCell><KeyStatus k={k} /></TableCell>
                    <TableCell className="text-right">
                      {!k.revoked_at && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmRevoke(k)}
                          data-testid={`revoke-${k.key_id}`}
                          className="text-neutral-600 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-1" /> Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* --- Create key modal ------------------------------------- */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-key-dialog">
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>
              Give the key a human-readable name so you can revoke the
              right one later. Think "Claude Desktop — Sagar" or
              "n8n automation".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="key-name">Name</Label>
            <Input
              id="key-name"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder="e.g. Claude Desktop — Sagar"
              maxLength={100}
              data-testid="create-key-name"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !creating) submitCreate();
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
              data-testid="create-key-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitCreate}
              disabled={creating || !nameDraft.trim()}
              data-testid="create-key-submit"
            >
              {creating ? "Creating…" : "Create key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Plaintext reveal modal (shown ONCE after create) ------ */}
      <Dialog
        open={!!justCreated}
        onOpenChange={(open) => { if (!open) setJustCreated(null); }}
      >
        <DialogContent data-testid="plaintext-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Key created — copy it now</DialogTitle>
            <DialogDescription>
              <span className="text-red-600 font-medium">
                This is the only time you'll see this key.
              </span>{" "}
              After you close this dialog it cannot be recovered.
              If you lose it, revoke it and create a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
              <div className="text-xs text-neutral-500 mb-1">Name</div>
              <div className="font-medium">{justCreated?.name}</div>
            </div>
            <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-neutral-500 mb-1">Key</div>
                  <code
                    className="block text-sm font-mono break-all"
                    data-testid="plaintext-value"
                  >
                    {justCreated?.plaintext}
                  </code>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={copyPlaintext}
                  data-testid="plaintext-copy"
                  className="shrink-0"
                >
                  {copied ? (
                    <><Check className="h-4 w-4 mr-1" /> Copied</>
                  ) : (
                    <><Copy className="h-4 w-4 mr-1" /> Copy</>
                  )}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setJustCreated(null)}
              data-testid="plaintext-done"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Revoke confirmation ----------------------------------- */}
      <Dialog
        open={!!confirmRevoke}
        onOpenChange={(open) => { if (!open) setConfirmRevoke(null); }}
      >
        <DialogContent data-testid="revoke-dialog">
          <DialogHeader>
            <DialogTitle>Revoke this key?</DialogTitle>
            <DialogDescription>
              Any client using <span className="font-mono">gos_{confirmRevoke?.prefix}…</span>{" "}
              ("{confirmRevoke?.name}") will immediately start getting
              401 responses. This cannot be undone — issue a new key if
              you need to restore access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmRevoke(null)}
              disabled={revoking}
              data-testid="revoke-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitRevoke}
              disabled={revoking}
              data-testid="revoke-confirm"
            >
              {revoking ? "Revoking…" : "Revoke key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
